import {Component, forwardRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Subject} from 'rxjs';
import {map, takeUntil} from 'rxjs/operators';
import {MessageTranslateService, TicketBenefit, TicketBenefitId, ticketBenefits} from '@shared/services';
import {AceLogger} from '@logging/ace-logger';
import {AceLoggerService} from '@logging/ace-logger.service';
import {I18nMessage} from '@shared/api/model/i18n';

const NONE_ID = -1 as const;
type BenefitSelectValue = TicketBenefitId | typeof NONE_ID;

@Component({
  selector: 'app-benefit-select',
  templateUrl: './benefit-select.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BenefitSelectComponent),
      multi: true
    }
  ],
  standalone: false
})
export class BenefitSelectComponent implements OnChanges, OnInit, OnDestroy, ControlValueAccessor {
  private readonly logger: AceLogger;
  private readonly ngUnsubscribe = new Subject<void>();
  private readonly BENEFIT_NONE_OPTION: TicketBenefit = {
    benefit_id: NONE_ID,
    benefit_name:  this.translateService.instant('None')
  };

  @Input() appendTo: string;
  @Input() clearable: boolean;
  @Input() isReadOnly: boolean;
  @Input() multiSelect = false;
  @Input() placeholder = 'Select Impact';
  @Input() showNoneOption = true;
  @Input() maxSelectedLabels: number;
  @Input() maxSelectedShowItemName = true;
  @Input() maxSelectedItemName: I18nMessage;
  @Input() truncateLabels = true;

  formControl = new FormControl<BenefitSelectValue | BenefitSelectValue[] | null>(null);
  benefitOptions: TicketBenefit[];

  onChange: (_) => void = (_) => {};
  onTouched: () => void = () => {};

  constructor(private translateService: MessageTranslateService, logService: AceLoggerService) {
    this.logger = logService.create(BenefitSelectComponent.name);
  }

  ngOnInit() {
    this.setBenefitOptions();
    this.formControl.valueChanges.pipe(
      takeUntil(this.ngUnsubscribe),
      map(value => this.coerceValueForConsumers(value)))
      .subscribe(value => this.onChange(value));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.multiSelect || changes.showNoneOption) {
      // multiSelect===true and showNoneOption===true should be mutually exclusive. If both are true, prioritize the most recently changed and
      // set the other to false
      if (this.multiSelect && this.showNoneOption) {
        if (changes.multiSelect) {
          this.showNoneOption = false;
          this.logger.warn('Both multiSelect and showNoneOption are true. Setting showNoneOption to false');
        } else {
          this.multiSelect = false;
          this.logger.warn('Both multiSelect and showNoneOption are true. Setting multiSelect to false');
        }
      }
      this.setBenefitOptions();
    }
  }

  private setBenefitOptions(): void {
    this.benefitOptions = this.showNoneOption
      ? ticketBenefits.concat([this.BENEFIT_NONE_OPTION])
      : ticketBenefits;
  }

  private coerceValueForConsumers(value: BenefitSelectValue | BenefitSelectValue[] | null): TicketBenefitId | TicketBenefitId[] | null {
    if (value === NONE_ID) {
      return null;
    }
    return <TicketBenefitId | TicketBenefitId[]>value;
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  writeValue(value: BenefitSelectValue | BenefitSelectValue[] | null): void {
    /** If value is null and showNoneOption is true, selects the None option (-1). Otherwise, uses the value as-is. */
    const transformedValue = value == null && this.showNoneOption ? NONE_ID : value;
    this.formControl.setValue(transformedValue, {emitEvent: false});
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.formControl.disable() : this.formControl.enable();
  }
}
